/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Capacity/DaysOffDialog" />

import { calculateNetDaysOff, calculateTotalNetDaysOff } from "Agile/Common/Utils/CapacityDateUtils";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import * as React from "react";
import { useState } from "react";
import { useObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { css } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { DatePicker } from "VSSUI/DatePicker";
import { Dialog } from "VSSUI/Dialog";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { DateRange } from "Wit/Clients/Work/Contracts/Work";
import { DateRangeTableItem, getDaysOffErrorMessage, runDateRangeOverlapValidation } from "Wit/SprintsHub/SprintsView/Utils/DaysOffValidationUtils";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { shiftToUTC } from "Wit/Common/DateUtils";

export interface IDaysDialogProps {
    daysOff: DateRange[];
    onDismiss: (updatedRanges?: DateRange[]) => void;
    selectedIteration?: IIterationData;
    teamDaysOff?: DateRange[];
    teamSettings?: ITeamSettings;
    user?: IdentityRef;
}

export function DaysOffDialog(props: IDaysDialogProps) {
    const [daysOffObservable, setDaysOffObservable] = useObservableArray<DateRangeTableItem>([...props.daysOff, { totalRow: true }]);

    const [areDatesInvalid, setDatesInvalid] = useState<boolean>(true);

    const validateDates = () => {
        runDateRangeOverlapValidation(daysOffObservable.value);

        if (areDatesInvalid !== daysOffObservable.value.some(daysOffRow => daysOffRow.errorMessage !== undefined)) {
            setDatesInvalid(!areDatesInvalid);
        }
    };

    const renderDateCell = (rowIndex: number, columnIndex: number, column: ITableColumn<DateRangeTableItem>, tableItem: DateRangeTableItem) => {
        const isStartCell = columnIndex === 0;

        if (tableItem.totalRow) {
            return isStartCell ? (
                <SimpleTableCell columnIndex={columnIndex} key="add-days-off-cell">
                    <Button iconProps={{ iconName: "Add" }} onClick={addDaysOff} subtle={true} text={Resources.AddDaysOff} />
                </SimpleTableCell>
            ) : (
                <SimpleTableCell columnIndex={columnIndex} key="total-days-off-end" />
            );
        }

        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                className="days-off-date-picker-cell"
                contentClassName="days-off-date-picker-content flex-start"
                key={`days-off-${isStartCell ? "start" : "end"}-${rowIndex}`}
                tableColumn={column}
            >
                <DatePicker
                    value={isStartCell ? tableItem.start : tableItem.end}
                    onChange={date => {
                        // Set the date then set the other date if it is undefined
                        const startDate = isStartCell ? date : tableItem.start ?? date;
                        const endDate = isStartCell ? tableItem.end ?? date : date;

                        const errorMessage = getDaysOffErrorMessage(
                            startDate,
                            endDate,
                            props.selectedIteration?.startDate,
                            props.selectedIteration?.finishDate
                        );

                        daysOffObservable.change(rowIndex, { start: startDate, end: endDate, errorMessage: errorMessage });

                        validateDates();
                    }}
                />
                {isStartCell && tableItem.errorMessage && (
                    <div className="flex-row flex-center error-text padding-top-4 font-size">
                        <Icon className="padding-right-4" iconName="Error" /> {tableItem.errorMessage}
                    </div>
                )}
            </SimpleTableCell>
        );
    };

    const addDaysOff = () => {
        // Add to the end before the total row
        daysOffObservable.splice(daysOffObservable.length - 1, 0, { start: undefined, end: undefined });
    };

    const columns: ITableColumn<DateRangeTableItem>[] = [
        {
            id: "start-time",
            name: Resources.Start,
            renderCell: renderDateCell,
            width: 250
        },
        {
            id: "end-time",
            name: Resources.End,
            renderCell: renderDateCell,
            width: 250
        },
        {
            id: "days-off",
            name: Resources.DaysOff,
            renderCell: (rowIndex: number, columnIndex: number, column: ITableColumn<DateRangeTableItem>, tableItem: DateRangeTableItem) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={`days-off-${rowIndex}`}>
                        <div className={css("net-days-off flex-row flex-grow justify-end flex-center", tableItem.totalRow && "font-weight-semibold")}>
                            {tableItem.totalRow ? (
                                <Observer daysOff={daysOffObservable}>
                                    {(obsProps: { daysOff: DateRangeTableItem[] }) => {
                                        return format(
                                            Resources.TotalDaysOff,
                                            calculateTotalNetDaysOff(
                                                obsProps.daysOff.filter(value => value.start !== undefined && value.end !== undefined) as DateRange[],
                                                props.teamSettings?.weekends.days ?? [],
                                                props.teamDaysOff,
                                                props.selectedIteration?.startDate ? shiftToUTC(props.selectedIteration.startDate) : undefined,
                                                props.selectedIteration?.finishDate ? shiftToUTC(props.selectedIteration.finishDate) : undefined
                                            )
                                        );
                                    }}
                                </Observer>
                            ) : tableItem.start === undefined || tableItem.end === undefined ? (
                                0
                            ) : (
                                calculateNetDaysOff(
                                    tableItem as DateRange,
                                    props.teamSettings?.weekends.days ?? [],
                                    props.teamDaysOff,
                                    props.selectedIteration?.startDate ? shiftToUTC(props.selectedIteration?.startDate!) : undefined,
                                    props.selectedIteration?.finishDate ? shiftToUTC(props.selectedIteration?.finishDate!) : undefined
                                )
                            )}
                        </div>
                    </SimpleTableCell>
                );
            },
            width: 75
        },
        {
            id: "delete",
            renderCell: (rowIndex: number, columnIndex: number, column: ITableColumn<DateRangeTableItem>, tableItem: DateRangeTableItem) => {
                if (tableItem.totalRow) {
                    return <SimpleTableCell columnIndex={columnIndex} key="total-delete" />;
                }

                return (
                    <SimpleTableCell columnIndex={columnIndex} key={`delete-${rowIndex}`}>
                        <Button
                            className="days-off-delete-button padding-8 bolt-table-cell-content-reveal"
                            iconProps={{ iconName: "Cancel" }}
                            onClick={() => {
                                daysOffObservable.splice(rowIndex, 1);
                                validateDates();
                            }}
                            subtle={true}
                            tooltipProps={{ text: Resources.Delete }}
                        />
                    </SimpleTableCell>
                );
            },
            width: 50
        }
    ];

    const onConfirm = () => {
        // Filter out empty rows
        props.onDismiss(daysOffObservable.value.filter(value => value.start !== undefined && value.end !== undefined) as DateRange[]);
    };

    return (
        <Dialog
            contentSize={ContentSize.Auto}
            lightDismiss={false}
            onDismiss={props.onDismiss}
            titleProps={{ text: format(Resources.DaysOffDialogTitle, props.user?.displayName) }}
            footerButtonProps={[
                { disabled: areDatesInvalid, onClick: onConfirm, primary: true, text: Resources.Ok },
                { onClick: () => props.onDismiss(), text: Resources.Cancel }
            ]}
        >
            <Table columns={columns} itemProvider={daysOffObservable} />
        </Dialog>
    );
}
